import { Box, Typography, LinearProgress, Chip, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";

/**
 * Componente para mostrar el progreso de aprobación de un batch
 * @param {object} summary - summary del batch con contadores
 * @param {boolean} showPercentage - Si mostrar porcentaje (default: true)
 * @param {boolean} showProgressBar - Si mostrar barra de progreso (default: true)
 * @param {string} size - Tamaño: "small" | "medium" | "large" (default: "medium")
 */
const BatchApprovalProgress = ({
  summary,
  showPercentage = true,
  showProgressBar = true,
  size = "medium",
}) => {
  if (!summary) return <Typography variant="caption">-</Typography>;

  const {
    totalPreDistributions = 0,
    approvedCount = 0,
    rejectedCount = 0,
    pendingCount = 0,
    readyForApprovalCount = 0,
  } = summary;

  // Calcular porcentaje de aprobación
  const approvalPercentage =
    totalPreDistributions > 0
      ? ((approvedCount / totalPreDistributions) * 100).toFixed(1)
      : 0;

  // Determinar color del progress bar
  const getProgressColor = () => {
    const percentage = parseFloat(approvalPercentage);
    if (percentage >= 90) return "success";
    if (percentage >= 50) return "info";
    if (percentage > 0) return "warning";
    return "inherit";
  };

  const progressColor = getProgressColor();

  // Tamaños según prop
  const sizes = {
    small: {
      chipSize: "small",
      typography: "caption",
      progressHeight: 4,
      spacing: 0.5,
    },
    medium: {
      chipSize: "small",
      typography: "body2",
      progressHeight: 6,
      spacing: 1,
    },
    large: {
      chipSize: "medium",
      typography: "body1",
      progressHeight: 8,
      spacing: 1.5,
    },
  };

  const sizeConfig = sizes[size] || sizes.medium;

  // Tooltip content
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" fontWeight="bold" display="block">
        Resumen de Aprobación
      </Typography>
      <Typography variant="caption" display="block">
        Total: {totalPreDistributions}
      </Typography>
      <Typography
        variant="caption"
        display="block"
        sx={{ color: "#66bb6a" }}
      >
        ✓ Aprobadas: {approvedCount} ({approvalPercentage}%)
      </Typography>
      {readyForApprovalCount > 0 && (
        <Typography
          variant="caption"
          display="block"
          sx={{ color: "#42a5f5" }}
        >
          ⏱ Listas: {readyForApprovalCount}
        </Typography>
      )}
      {pendingCount > 0 && (
        <Typography
          variant="caption"
          display="block"
          sx={{ color: "#ffa726" }}
        >
          ⏳ Pendientes: {pendingCount}
        </Typography>
      )}
      {rejectedCount > 0 && (
        <Typography
          variant="caption"
          display="block"
          sx={{ color: "#ef5350" }}
        >
          ✗ Rechazadas: {rejectedCount}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow>
      <Box sx={{ minWidth: 120 }}>
        {/* Progress Bar */}
        {showProgressBar && (
          <Box sx={{ mb: sizeConfig.spacing }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(parseFloat(approvalPercentage), 100)}
              color={progressColor}
              sx={{
                height: sizeConfig.progressHeight,
                borderRadius: 1,
                backgroundColor: "rgba(0,0,0,0.1)",
              }}
            />
          </Box>
        )}

        {/* Chips con contadores */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {/* Aprobadas */}
          <Chip
            icon={<CheckCircleIcon />}
            label={approvedCount}
            color="success"
            size={sizeConfig.chipSize}
            variant="filled"
          />

          {/* Listas para aprobación */}
          {readyForApprovalCount > 0 && (
            <Chip
              label={readyForApprovalCount}
              color="info"
              size={sizeConfig.chipSize}
              variant="outlined"
            />
          )}

          {/* Pendientes */}
          {pendingCount > 0 && (
            <Chip
              icon={<PendingIcon />}
              label={pendingCount}
              color="warning"
              size={sizeConfig.chipSize}
              variant="outlined"
            />
          )}

          {/* Rechazadas */}
          {rejectedCount > 0 && (
            <Chip
              icon={<CancelIcon />}
              label={rejectedCount}
              color="error"
              size={sizeConfig.chipSize}
              variant="filled"
            />
          )}
        </Box>

        {/* Porcentaje */}
        {showPercentage && (
          <Typography
            variant={sizeConfig.typography}
            color="text.secondary"
            align="center"
            sx={{ mt: 0.5 }}
          >
            {approvalPercentage}% aprobado
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default BatchApprovalProgress;
